import { useAuthContext } from '@/context'
import { NODE_BASE_URL } from '@/services/api'
import * as Ably from 'ably'
import { AblyProvider } from 'ably/react'
import { useEffect, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

/* eslint-disable react-refresh/only-export-components -- hoc module */

const TAB_INSTANCE_ID = uuidv4()

const MAX_CONNECTION_FAILURES = 5
const RETRY_DELAY_MS = 30000

const createAblyClient = (clientId: string) => {
    const instanceClientId = `${clientId}:${TAB_INSTANCE_ID}`

    return new Ably.Realtime({
        clientId: instanceClientId,
        authCallback: async (tokenParams: any, callback: any) => {
            try {
                const url = new URL(`${NODE_BASE_URL}/createTokenRequest`)
                const params = { ...(tokenParams || {}), clientId: instanceClientId }
                for (const [k, v] of Object.entries(params)) {
                    if (v === undefined || v === null) continue
                    url.searchParams.set(k, String(v))
                }

                const resp = await fetch(url.toString(), {
                    method: 'GET',
                    credentials: 'include',
                })
                if (!resp.ok) {
                    const text = await resp.text().catch(() => '')
                    throw new Error(`Token request failed (${resp.status}): ${text || resp.statusText}`)
                }
                const body = await resp.json()
                callback(null, body)
            } catch (err) {
                callback(err, null)
            }
        },
        recover: (_: any, cb: any) => cb(false),
        disconnectedRetryTimeout: RETRY_DELAY_MS,
        suspendedRetryTimeout: 60000,
        httpRequestTimeout: 15000,
        realtimeRequestTimeout: 10000,
    } as any)
}

export default function AblyProviderWrapper({ children }: { children: React.ReactNode }) {
    const { user } = useAuthContext()
    const clientRef = useRef<Ably.Realtime | null>(null)
    const failureCountRef = useRef(0)
    const [ably, setAbly] = useState<Ably.Realtime | null>(null)

    useEffect(() => {
        const userId = user?.id
        if (!userId) {
            if (clientRef.current) {
                clientRef.current.connection.off()
                clientRef.current.close()
                clientRef.current = null
                setAbly(null)
            }
            failureCountRef.current = 0
            return
        }

        if (clientRef.current) {
            const existingId = (clientRef.current as any).auth?.clientId?.split?.(':')?.[0]
            if (existingId === userId) return
            clientRef.current.connection.off()
            clientRef.current.close()
            clientRef.current = null
            failureCountRef.current = 0
        }

        const client = createAblyClient(userId)
        clientRef.current = client
        setAbly(client)

        client.connection.on('connected', () => {
            failureCountRef.current = 0
            if (import.meta.env.DEV) console.log('Connected to Ably', { userId })
        })

        client.connection.on('disconnected', () => {
            if (import.meta.env.DEV) console.log('Disconnected from Ably', { userId })
        })

        client.connection.on('failed', (err: any) => {
            failureCountRef.current += 1
            const n = failureCountRef.current
            console.warn(`Ably connection failed (${n}/${MAX_CONNECTION_FAILURES})`, err?.message ?? err)
            if (n >= MAX_CONNECTION_FAILURES) {
                console.warn('Ably: too many failures, stopping reconnects. Refresh the page to try again.')
                client.connection.off()
                client.close()
                clientRef.current = null
                setAbly(null)
            }
        })

        return () => {
            client.connection.off()
            Promise.resolve(client.close()).catch(() => {})
            if (clientRef.current === client) {
                clientRef.current = null
                setAbly(null)
            }
        }
    }, [user?.id])

    if (user && !ably) {
        return (
            <div className="flex items-center justify-center min-h-[200px]">
                <span className="text-sm text-muted-foreground">Connecting…</span>
            </div>
        )
    }
    if (!user || !ably) return <>{children}</>

    return <AblyProvider client={ably}>{children}</AblyProvider>
}

export const getTabInstanceId = () => TAB_INSTANCE_ID
